import React, { Fragment, useReducer, useEffect } from "react";
import DatePicker from "react-date-picker";
import { useAuth0 } from "@auth0/auth0-react";

import ai from "../../helpers/axios";
import DataTable from "react-data-table-component";
import AsyncSelect from "react-select/async";
import RFPForm from "../leads/rfp-form";

import oneWayColumns from "./one-way-result-columns";
import roundTripColumns from "./round-trip-result-columns";

import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import "react-json-pretty/themes/monikai.css";
import JSONPretty from "react-json-pretty";
import * as Sentry from "@sentry/react";
import "./calculator-seasonal-strip.css";
import "./calculator-peak-strip.css";

const reducer = (state, action) => {
  switch (action.type) {
    case "fieldChanged": {
      let value =
        action.fieldType === "number" ? parseInt(action.value) : action.value;

      return {
        ...state,
        [action.field]: value,
      };
    }

    case "leadFieldChanged": {
      let value =
        action.fieldType === "number" ? parseInt(action.value) : action.value;

      return {
        ...state,
        leadData: {
          ...state.leadData,
          [action.field]: value,
        },
      };
    }
    case "airportChange": {
      return {
        ...state,
        [action.airportType]: action.payload,
      };
    }

    case "owChange": {
      let field =
        action.field === "departureOW" ? "departureAirport" : "arrivalAirport";

      return {
        ...state,
        [field]: {
          airportName: state[field].airportName,
          id: state[field].id,
          code: state[field].code,
          ow: action.value,
        },
      };
    }

    case "tripTypeChanged": {
      return {
        ...state,
        tripType: action.payload.tripType,
        returnDate: action.payload.tripType === 1 ? null : state.returnDate,
      };
    }

    case "estimateReturned": {
      return {
        ...state,
        tableData: action.payload.results.tripOptions,
        data: action.payload,
      };
    }

    default:
      break;
  }
  return state;
};

const initialState = {
  departureAirport: {
    airportName: null,
    id: null,
    code: null,
    ow: "",
  },
  arrivalAirport: {
    airportName: null,
    id: null,
    code: null,
    ow: "",
  },
  passengers: 0,
  departureDate: null,
  tripType: 1,
  returnDate: null,
  data: {},
  composedOW: "",
  tableData: [],
  departureAirportInput: "",
  arrivalAirportInput: "",
  leadData: {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    companyName: "",
    comments: "",
  },
};

const CalculatorContent = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const history = useHistory();

  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });
    let estimateData = {
      departureAirport: state.departureAirport,
      arrivalAirport: state.arrivalAirport,
      passengers: state.passengers,
      departureDate: state.departureDate,
      tripType: state.tripType,
      returnDate: state.returnDate,
      origin: "flightDeckInternal",
      owRatio: state.composedOW,
    };

    console.log("estimateData", estimateData);

    let { data } = await ai
      .auth(token)
      .post(`/api/estimates/create`, estimateData)
      .catch((error) => {
        console.error(error);
        Sentry.captureException(error);
      });

    dispatch({
      type: "estimateReturned",
      payload: data,
    });

    console.log(data);
  };

  const loadAirportOptions = async (inputValue) => {
    let ip =
      Math.floor(Math.random() * 255) +
      1 +
      "." +
      (Math.floor(Math.random() * 255) + 0) +
      "." +
      (Math.floor(Math.random() * 255) + 0) +
      "." +
      (Math.floor(Math.random() * 255) + 0);
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });
    let { data } = await ai
      .auth(token)
      .get(`/api/airports/search?q=${inputValue}&ip_address=${ip}`);

    console.log(data);

    let options = [];
    if (data.length >= 1) {
      options = data.map((i) => ({
        value: i._id,
        label: i.airportName,
        code: i.code,
        ow: i.ow,
      }));
    }

    return options;
  };

  const getOWRatio = async () => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    if (state.departureAirport.id && state.arrivalAirport.id) {
      let { data } = await ai
        .auth(token)
        .get(
          `/api/airports/getOWRatio?departureAirportId=${state.departureAirport.id}&arrivalAirportId=${state.arrivalAirport.id}`
        )
        .catch((error) => console.error(error.response.data));

      if (data) {
        console.log("Composed OW", data);

        dispatch({
          type: "fieldChanged",
          field: "composedOW",
          value: parseFloat(data.ow) || "",
        });
      }

      return;
    }
  };

  useEffect(() => {
    getOWRatio();
    document.title = "Calculator | evoJets HQ";
  }, [state.departureAirport.id, state.arrivalAirport.id]);

  const handleDepartureChange = (selectedAirport) => {
    dispatch({
      type: "airportChange",
      airportType: "departureAirport",
      payload: {
        airportName: selectedAirport?.label || "",
        id: selectedAirport?.value || "",
        code: selectedAirport?.code || "",
        ow: parseFloat(selectedAirport?.ow) || "",
      },
    });
  };

  const handleArrivalChange = (selectedAirport) => {
    dispatch({
      type: "airportChange",
      airportType: "arrivalAirport",
      payload: {
        airportName: selectedAirport?.label || "",
        id: selectedAirport?.value || "",
        code: selectedAirport?.code || "",
        ow: parseFloat(selectedAirport?.ow) || "",
      },
    });
  };

  const handleRFPSubmit = async (e) => {
    e.preventDefault();

    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });
    let leadData = {
      firstName: state.leadData.firstName,
      lastName: state.leadData.lastName,
      email: state.leadData.email,
      phone: state.leadData.phone,
      companyName: state.leadData.companyName,
      comments: state.leadData.comments,
      estimateId: state.data.estimateDoc._id,
      tripType: state.data.estimateDoc.tripType,
      leadOrigin: "flightDeckInternal",
    };
    let { data } = await ai.auth(token).post(`/api/leads/create`, leadData);

    // dispatch({
    //   type: "leadReturned",
    //   payload: data,
    // });

    setTimeout(() => {
      toast.success("Lead Created Successfully!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }, 50);

    history.push({ pathname: "/leads/" });

    console.log(data);
  };

  const handleAirportChange = (e) => {
    dispatch({
      type: "owChange",
      field: e.target.name,
      value: parseFloat(e.target.value) || "",
      fieldType: "float",
    });
  };

  return (
    <Fragment>
      <div className="text-center hero">
        <h1 className="mb-4">evoJets HQ Calculator</h1>
      </div>

      <form className="calculatorForm" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="tripType">Trip Type</label>
          <select
            id="tripType"
            value={state.tripType}
            onChange={(e) =>
              dispatch({
                type: "tripTypeChanged",
                payload: {
                  tripType: parseInt(e.currentTarget.value),
                },
              })
            }
            name="tripType"
            required
          >
            <option key="placeholder" value={0} hidden>
              Trip Type
            </option>

            <option key="oneWay" value={1}>
              One-Way
            </option>
            <option key="roundTrip" value={2}>
              Round Trip
            </option>
          </select>
        </div>

        <div className="form-group">
          <label>Departure Airport</label>

          <AsyncSelect
            className="departure-airport-select"
            classNamePrefix="select"
            cacheOptions
            loadOptions={loadAirportOptions}
            // defaultValue={props.state.selectedAircraft}
            placeholder="Search Airports"
            // onInputChange={props.handleDepartureInputChange}
            onChange={handleDepartureChange}
          ></AsyncSelect>
        </div>

        <div className="form-group">
          <label>Arrival Airport</label>

          <AsyncSelect
            className="arrival-airport-select"
            classNamePrefix="select"
            cacheOptions
            loadOptions={loadAirportOptions}
            // defaultValue={props.state.selectedAircraft}
            placeholder="Search Airports"
            // onInputChange={props.handleDepartureInputChange}
            onChange={handleArrivalChange}
          ></AsyncSelect>
        </div>

        <div className="form-group">
          <label htmlFor="passengers">Passengers</label>
          <input
            type="number"
            name="passengers"
            placeholder="Passengers"
            onChange={(e) =>
              dispatch({
                type: "fieldChanged",
                field: e.target.name,
                value: e.target.value,
                fieldType: e.target.type,
              })
            }
          ></input>
        </div>

        <div className="form-group">
          <label htmlFor="departureDate">Departure Date</label>

          <DatePicker
            name="departureDate"
            onChange={(date) =>
              dispatch({
                type: "fieldChanged",
                field: "departureDate",
                value: date,
              })
            }
            value={state.departureDate}
          ></DatePicker>
        </div>

        {state.tripType === 2 && (
          <div className="form-group">
            <label htmlFor="returnDate">Return Date</label>

            <DatePicker
              name="returnDate"
              onChange={(date) =>
                dispatch({
                  type: "fieldChanged",
                  field: "returnDate",
                  value: date,
                })
              }
              value={state.returnDate}
            ></DatePicker>
          </div>
        )}

        <div className="form-group">
          <label htmlFor="departureOW">Departure OW Ratio</label>
          <input
            type="number"
            name="departureOW"
            placeholder="Departure OW Ratio"
            onChange={handleAirportChange}
            value={state.departureAirport.ow}
          ></input>
        </div>

        <div className="form-group">
          <label htmlFor="arrivalOW">Arrival OW Ratio</label>
          <input
            type="number"
            name="arrivalOW"
            placeholder="Arrival OW Ratio"
            onChange={handleAirportChange}
            value={state.arrivalAirport.ow}
          ></input>
        </div>

        <div className="form-group">
          <label htmlFor="composedOW">Dynamic OW Ratio</label>
          <input
            type="number"
            name="composedOW"
            min="0.5"
            max="5"
            step="0.01"
            placeholder="Dynamic OW Ratio"
            onChange={(e) => {
              dispatch({
                type: "fieldChanged",
                field: e.target.name,
                value: parseFloat(e.target.value) || "",
                fieldType: "float",
              });
            }}
            value={state.composedOW}
          ></input>
        </div>

        <input type="submit" value="Submit"></input>
      </form>

      {state.tableData.length ? (
        <h4 className="centered">
          Distance: {Math.round(state.data.results.distance)} mi.
        </h4>
      ) : (
        ""
      )}

      {state.data?.results?.seasonalPricing?.rules?.length > 0 && (
        <div className="calculator-seasonal-strip">
          <div className="calculator-seasonal-strip__header">
            <span className="calculator-seasonal-strip__heading">
              Seasonal Pricing
            </span>
            <span className="calculator-seasonal-strip__count">
              {state.data.results.seasonalPricing.rules.length} rule
              {state.data.results.seasonalPricing.rules.length === 1 ? "" : "s"}
            </span>
          </div>
          {state.data.results.seasonalPricing.rules.map((r) => {
            const deltaClass =
              r.adjustment > 0
                ? "calculator-seasonal-strip__delta--positive"
                : r.adjustment < 0
                ? "calculator-seasonal-strip__delta--negative"
                : "";
            return (
              <div key={r._id} className="calculator-seasonal-strip__item">
                <span className="calculator-seasonal-strip__label">{r.label}</span>
                <span
                  className={`calculator-seasonal-strip__delta ${deltaClass}`.trim()}
                >
                  {r.adjustment > 0 ? "+" : r.adjustment < 0 ? "−" : ""}
                  {Math.abs(r.adjustment * 100).toFixed(2)}%
                </span>
                {r.explainer && (
                  <div className="calculator-seasonal-strip__explainer">
                    {r.explainer}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {(() => {
        const results = state.data?.results;
        if (!results) return null;
        const tripType = results.tripType;
        const tierClass = (cat) =>
          cat <= 1 ? "green" : cat <= 3 ? "yellow" : "red";
        const tierLabel = (cat) =>
          cat >= 4 ? "High Peak" : cat >= 2 ? "Peak" : "Low Peak";

        const renderHeader = (category) => {
          const tier = tierClass(category);
          return (
            <div className="calculator-peak-strip__header">
              <span className="calculator-peak-strip__pretitle">Peak Value</span>
              <span
                className={`calculator-peak-strip__severity calculator-peak-strip__severity--${tier}`}
              >
                {tierLabel(category)}
              </span>
            </div>
          );
        };

        const renderMeter = (category) => (
          <span
            className="calculator-peak-strip__meter"
            aria-label={`${category} of 5`}
          >
            {[1, 2, 3, 4, 5].map((i) => (
              <span
                key={i}
                className={
                  "calculator-peak-strip__dot" +
                  (i <= category ? " calculator-peak-strip__dot--filled" : "")
                }
              />
            ))}
          </span>
        );

        const renderValue = (category) => (
          <span className="calculator-peak-strip__value">
            {category}
            <span className="calculator-peak-strip__value-total"> / 5</span>
          </span>
        );

        if (tripType === 1 && results.peakDay?.peakLabel) {
          const { category } = results.peakDay.peakLabel;
          const tier = tierClass(category);
          return (
            <div className="calculator-peak-strip">
              {renderHeader(category)}
              <div className="calculator-peak-strip__rows">
                <div
                  className={`calculator-peak-strip__row calculator-peak-strip__row--solo calculator-peak-strip__row--${tier}`}
                >
                  {renderMeter(category)}
                  {renderValue(category)}
                </div>
              </div>
            </div>
          );
        }

        if (tripType === 2 && results.peakDays) {
          const { departureDay, returnDay, otgRate } = results.peakDays;
          if (!departureDay && !returnDay) return null;
          const inUseCategory = otgRate?.category;
          const renderRow = (kind, day) => {
            if (!day) return null;
            const isActive = day.category === inUseCategory;
            const tier = tierClass(day.category);
            return (
              <div
                key={kind}
                className={`calculator-peak-strip__row calculator-peak-strip__row--${tier}`}
              >
                <span className="calculator-peak-strip__leg">{kind}</span>
                {renderMeter(day.category)}
                {renderValue(day.category)}
                {isActive && (
                  <span className="calculator-peak-strip__in-use">In use</span>
                )}
              </div>
            );
          };
          return (
            <div className="calculator-peak-strip">
              {inUseCategory != null && renderHeader(inUseCategory)}
              <div className="calculator-peak-strip__rows">
                {renderRow("Departure", departureDay)}
                {renderRow("Return", returnDay)}
              </div>
            </div>
          );
        }

        return null;
      })()}

      <div id="results">
        {state.tableData.length && state.data.results.tripType === 1 ? (
          <DataTable
            data={state.tableData}
            columns={oneWayColumns}
            noHeader
            highlightOnHover
            pagination={false}
            responsive={true}
            defaultSortField={"lowSpread"}
          ></DataTable>
        ) : (
          ""
        )}

        {state.tableData.length && state.data.results.tripType === 2 ? (
          <DataTable
            data={state.tableData}
            columns={roundTripColumns}
            noHeader
            highlightOnHover
            pagination={false}
            responsive={true}
            defaultSortField={"lowSpread"}
          ></DataTable>
        ) : (
          ""
        )}

        {/* {state.data.estimateDoc && (
          <RFPForm
            handleRFPSubmit={handleRFPSubmit}
            dispatch={dispatch}
            state={state}
          ></RFPForm>
        )} */}

        {/* <JSONPretty id="json-pretty" data={state.data.results}></JSONPretty> */}

        {/* <JSONPretty id="json-pretty" data={state.departureAirport}></JSONPretty>

        // <JSONPretty id="json-pretty" data={state.leadData}></JSONPretty>
        <JSONPretty id="json-pretty" data={state.departureAirport}></JSONPretty> */}
      </div>
    </Fragment>
  );
};

export default CalculatorContent;
